import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Alert } from 'react-bootstrap';
import ChannelService
  from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import { selectThresholdRangeById } from '../../../../redux/slices/thresholdRangeSlice/thresholdRangeSlice';
import {
  selectActionsData,
  selectNameAlertData,
  selectReviewData,
  selectSensorsData,
  selectThresholdData,
} from '../../../../redux/slices/alertFormSlice/alertFormSlice';
import { selectNotificationGroupList } from '../../../../redux/slices/notificationGroupsSlice/notificationGroupsSlice';
import ReviewBulkAlertBox from './ReviewBulkAlertBox';
import AlertNamesTable from './AlertNamesTable';
import { formatThresholdDetails, getAlertRowDisplayData, getThresholdSource } from '../AlertFormUtility';
import { selectRoomAlertDeviceList, selectSensorMap } from '../../../../redux/slices/deviceList/deviceListSlice';
import { selectSessionInformation } from '../../../../redux/slices/sessionSlice';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function BulkAlertFormReview() {
  const deviceList = useSelector(selectRoomAlertDeviceList);
  const sensorMap = useSelector(selectSensorMap);
  const sensorsData = useSelector(selectSensorsData);
  const thresholdData = useSelector(selectThresholdData);
  const actionData = useSelector(selectActionsData);
  const nameAlertData = useSelector(selectNameAlertData);
  const reviewData = useSelector(selectReviewData);
  const sessionInformation = useSelector(selectSessionInformation);
  const combinedNotificationGroupIdList = actionData
    .actions.flatMap((action) => action.notificationGroupIdList);
  const failedChannelKeys = reviewData.failedChannelKeys || [];
  const { unexpectedError } = reviewData;
  const alertTags = nameAlertData && nameAlertData.alertTags;

  const notificationGroupList = useSelector(selectNotificationGroupList);

  const selectedChannels = asArray(sensorsData && sensorsData.selectedChannels);

  const alertNamePairs = asArray(nameAlertData && nameAlertData.alertNamePairs);

  const alertCount = selectedChannels.length;

  const thresholdId = thresholdData ? thresholdData.thresholdId : -1;
  const hasThresholdId = thresholdId !== -1 && thresholdId !== null && typeof thresholdId !== 'undefined';
  const thresholdRangeById = useSelector((state) => selectThresholdRangeById(state, thresholdId));
  const existingThresholdRange = hasThresholdId && thresholdRangeById ? thresholdRangeById : null;

  const thresholdSource = getThresholdSource(sensorsData, thresholdData, existingThresholdRange);
  const thresholdName = thresholdSource.thresholdName || 'Custom Threshold';

  const channelImpl = useMemo(
    () => (sensorsData.selectedChannels[0]
      ? ChannelService.getChannelFromChannelKey(sensorsData.selectedChannels[0])
      : null),
    [sensorsData.selectedChannels],
  );

  const thresholdDetails = useMemo(
    () => formatThresholdDetails(
      thresholdSource.readingType,
      thresholdSource.thresholdOperator,
      thresholdSource.thresholdValue,
      sessionInformation,
      channelImpl,
    ),
    [thresholdSource.readingType,
      thresholdSource.thresholdOperator, thresholdSource.thresholdValue],
  );

  const resolvedTagNames = useMemo(
    () => asArray(alertTags)
      .map((tag) => (tag && tag.name ? tag.name : null))
      .filter(Boolean),
    [alertTags],
  );

  const tagsText = resolvedTagNames.length ? resolvedTagNames.join(', ') : 'None';
  const showTags = alertTags && typeof alertTags.size === 'function'
    ? alertTags.size() > 0
    : resolvedTagNames.length > 0;

  const notificationGroupsText = useMemo(
    () => asArray(notificationGroupList)
      .filter((g) => combinedNotificationGroupIdList.includes(g.id))
      .map((g) => g.name)
      .filter(Boolean)
      .join(', '),
    [notificationGroupList, combinedNotificationGroupIdList],
  );
  const hasFailures = (failedChannelKeys && failedChannelKeys.length > 0) || unexpectedError;
  const successCount = selectedChannels.length - (failedChannelKeys ? failedChannelKeys.length : 0);
  const hasActions = actionData.actions.length > 0;
  const failedAlertRows = useMemo(() => {
    if (!hasFailures) return [];
    return alertNamePairs
      .filter((pair) => failedChannelKeys.includes(pair.channelKey))
      .map((pair) => getAlertRowDisplayData(
        pair.channelKey,
        pair.alertName,
        deviceList,
        sensorMap,
      ));
  }, [alertNamePairs, failedChannelKeys, hasFailures, deviceList, sensorMap]);

  return (
    <div className="container-fluid">
      <div className="mb-2">
        <h6>Review the new alerts you&apos;re about to create.</h6>
      </div>

      <ReviewBulkAlertBox
        alertCount={alertCount}
        thresholdName={thresholdName}
        thresholdDetails={thresholdDetails}
        showTags={showTags}
        tagsText={tagsText}
        notificationGroupsText={notificationGroupsText}
        hasActions={hasActions}
      />

      <AlertNamesTable
        alertNamePairs={alertNamePairs}
      />
      {(hasFailures || unexpectedError) && (
        <div className="mt-4">
          <Alert variant="danger" className="mb-3">
            {hasFailures ? (
              <>
                <div className="mb-0">
                  <strong>{successCount}</strong>
                  {' '}
                  alerts created successfully.
                  {' '}
                  <strong>{failedChannelKeys.length}</strong>
                  {' '}
                  alerts skipped due to errors:
                </div>
                <div className="ml-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {failedAlertRows.map((row) => (
                    <div key={row.key} className="mb-0">
                      <strong>{row.alertName}</strong>
                      {' '}
                      {row.details}
                    </div>
                  ))}
                </div>
              </>
            ) : (

              <div className="mb-0">
                An unexpected error has occurred, please try again or contact support.
              </div>
            )}
          </Alert>
        </div>
      )}
    </div>
  );
}

export default BulkAlertFormReview;
